import * as React from 'react';
import _ from 'lodash';
import { endOfDay, startOfDay, format } from 'date-fns';
import type { Props as CalendarInputProps } from '../../CalendarInput/CalendarInput';
import CalendarInput from '../../CalendarInput/CalendarInput';
import Dropdown from '../../Dropdown/Dropdown';
import type { DateFilterItem, FilterItem } from '../../../../types/Filter';
import { AddFilterDropdown } from '../AddFilter/AddFilter';
import type { OnFilterChangeHandler } from '../types';
import DeleteFilterButton from './DeleteFilterButton';
import Styles from './filter-views.module.scss';

type Props = {
    filterItem: DateFilterItem;
    availableFilters: FilterItem[];
    onChange: OnFilterChangeHandler;
};

const DROPDOWN_ITEMS = [
    { name: 'Is', value: 'eq' },
    { name: 'Between', value: 'between' },
    { name: 'After or equals', value: 'gte' },
    { name: 'Before or equals', value: 'lte' }
];

// convert dates to UTC format because server has to work with UTC dates
const startUtcDate = (date: Date) => format(startOfDay(date), "yyyy-MM-dd'T'HH:mm:ss'Z'");

const endUtcDate = (date: Date) => format(endOfDay(date), "yyyy-MM-dd'T'HH:mm:ss'Z'");

// convert UTC date to local date because if create date from string directly, Date will add timezone offset and screw calendar date
// e.g. end of date is 3rd of Oct 23:59:59 in UTC, but in local time it will be 4th of October 02:59:59
// add timezone offset date.getTimezoneOffset(), which is for example above is "-3h".
// so "3rd of Oct 23:59:59 in UTC - 3h" equals "3rd of Oct 20:59:59"
// new Date(3rd of Oct 20:59:59) will be 3rd of Oct 23:59:59
// calendar don't care about hours so date will be rendered as 3rd of Oct which is what we want
function convertUTCToLocalDate(date: string | Date): Date {
    date = new Date(date);
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
}

const DateFilterView = (props: Props) => {
    const { filterItem } = props;
    let calendarProps: CalendarInputProps;

    if (filterItem.operator === 'between') {
        calendarProps = {
            mode: 'range',
            value: filterItem.startDate
                ? {
                      from: convertUTCToLocalDate(filterItem.startDate),
                      to: filterItem.endDate ? convertUTCToLocalDate(filterItem.endDate) : undefined
                  }
                : undefined,
            onChange: (dates) => {
                props.onChange({
                    ...filterItem,
                    startDate: dates.from ? startUtcDate(dates.from) : undefined,
                    endDate: dates.to ? endUtcDate(dates.to) : undefined
                });
            }
        };
    } else {
        calendarProps = {
            mode: 'single',
            value: filterItem.date ? convertUTCToLocalDate(filterItem.date) : undefined,
            onChange: (date) => {
                props.onChange({
                    ...filterItem,
                    date: filterItem.operator === 'lte' ? endUtcDate(date) : startUtcDate(date)
                });
            }
        };
    }

    const selectedOperatorItem = DROPDOWN_ITEMS.find((item) => item.value === filterItem.operator);

    return (
        <div className={Styles['filter-view']}>
            <AddFilterDropdown
                availableFilters={props.availableFilters}
                value={filterItem}
                onAdd={(filterItem) => filterItem !== props.filterItem && props.onChange(filterItem)}
            />
            <Dropdown
                options={DROPDOWN_ITEMS}
                value={filterItem.operator}
                menuClassName={Styles['filter-view-dropdown-tooltip']}
                className={Styles.control}
                onChange={(operator) => {
                    let newFilterItem;
                    if (operator === 'between') {
                        newFilterItem = {
                            ..._.omit(filterItem, ['date']),
                            operator
                        };
                    } else {
                        let date = 'date' in filterItem && filterItem.date ? filterItem.date : undefined;
                        if (date && (operator === 'gte' || operator === 'eq')) {
                            date = startUtcDate(convertUTCToLocalDate(date));
                        } else if (date && operator === 'lte') {
                            date = endUtcDate(convertUTCToLocalDate(date));
                        }
                        newFilterItem = {
                            ..._.omit(filterItem, ['startDate', 'endDate']),
                            operator,
                            date
                        };
                    }
                    if (newFilterItem.operator !== filterItem.operator) {
                        props.onChange(newFilterItem as DateFilterItem);
                    }
                }}
                dataTip={selectedOperatorItem?.name}
                dataTipOverflow
            />
            <CalendarInput
                placeholder={filterItem.operator === 'between' ? 'Select date range' : 'Select date'}
                inputClassName={Styles.control}
                {...calendarProps}
            />
            <DeleteFilterButton {...props} />
        </div>
    );
};

export default DateFilterView;
